import React, { useState } from "react";
import { View, ScrollView, StyleSheet, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { locationService } from "@/services/locationService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { MarketPicker } from "@/components/Pickers/MarketPicker";
import { supabase } from "@/lib/supabase-client";

export default function NewLocationPage() {
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const toast = useToast();

  const [saving, setSaving] = useState(false);
  const [showMarketPicker, setShowMarketPicker] = useState(false);
  const [marketId, setMarketId] = useState<string | null>(null);
  const [marketName, setMarketName] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    instructions: "",
  });

  const handleCreate = async () => {
    if (!tenantId) {
      toast.showToast({
        title: "Error",
        message: "Tenant ID not found",
        toastType: "error",
      });
      return;
    }

    if (
      !formData.name.trim() ||
      !formData.street.trim() ||
      !formData.city.trim() ||
      !formData.state.trim() ||
      !formData.zip.trim()
    ) {
      toast.showToast({
        title: "Validation Error",
        message: "Name, street, city, state, and ZIP are required",
        toastType: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const newLocation = await locationService.addLocation({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        street: formData.street.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        zip: formData.zip.trim(),
        instructions: formData.instructions.trim() || null,
        tenant_id: tenantId,
        market_id: marketId,
      });

      toast.showToast({
        title: "Location created",
        message: "Location has been successfully created",
        toastType: "success",
      });

      router.push(`/settings/locations/${newLocation.id}`);
    } catch (error: any) {
      toast.showToast({
        title: "Creation failed",
        message: error?.message || "Failed to create location",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => router.push("/(app)/settings/locations")} style={styles.backButton}>
              <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
            </TouchableOpacity>
            <View>
              <ThemedText type="title">New Location</ThemedText>
              <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>Create a new location</ThemedText>
            </View>
          </View>

          <View style={styles.headerActions}>
            <Button
              text="Cancel"
              onPress={() => router.push("/(app)/settings/locations")}
              style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
            />
            <Button
              text={saving ? "Creating..." : "Create"}
              onPress={handleCreate}
              disabled={saving}
              style={[styles.actionButton, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
            />
          </View>
        </View>
      </View>

      {/* Form */}
      <ScrollView style={styles.content}>
        <View style={styles.section}>
          <View style={styles.inputGroup}>
            <ThemedText style={styles.label}>Name *</ThemedText>
            <ThemedInput
              value={formData.name}
              onChangeText={(text) => setFormData({ ...formData, name: text })}
              placeholder="Enter location name"
              style={styles.input}
            />
          </View>

          <View style={styles.inputGroup}>
            <ThemedText style={styles.label}>Description</ThemedText>
            <ThemedInput
              value={formData.description}
              onChangeText={(text) => setFormData({ ...formData, description: text })}
              placeholder="Enter description"
              multiline
              numberOfLines={3}
              style={styles.input}
            />
          </View>

          <View style={styles.inputGroup}>
            <ThemedText style={styles.label}>Street *</ThemedText>
            <ThemedInput
              value={formData.street}
              onChangeText={(text) => setFormData({ ...formData, street: text })}
              placeholder="Enter street address"
              style={styles.input}
            />
          </View>

          <View style={styles.row}>
            <View style={[styles.inputGroup, { flex: 1 }]}>
              <ThemedText style={styles.label}>City *</ThemedText>
              <ThemedInput
                value={formData.city}
                onChangeText={(text) => setFormData({ ...formData, city: text })}
                placeholder="City"
                style={styles.input}
              />
            </View>

            <View style={[styles.inputGroup, { width: 80 }]}>
              <ThemedText style={styles.label}>State *</ThemedText>
              <ThemedInput
                value={formData.state}
                onChangeText={(text) => setFormData({ ...formData, state: text.toUpperCase() })}
                placeholder="ST"
                maxLength={2}
                style={styles.input}
              />
            </View>

            <View style={[styles.inputGroup, { width: 100 }]}>
              <ThemedText style={styles.label}>ZIP *</ThemedText>
              <ThemedInput
                value={formData.zip}
                onChangeText={(text) => setFormData({ ...formData, zip: text })}
                placeholder="ZIP"
                keyboardType="numeric"
                style={styles.input}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <ThemedText style={styles.label}>Instructions</ThemedText>
            <ThemedInput
              value={formData.instructions}
              onChangeText={(text) => setFormData({ ...formData, instructions: text })}
              placeholder="Enter special instructions"
              multiline
              numberOfLines={3}
              style={styles.input}
            />
          </View>

          <View style={styles.inputGroup}>
            <ThemedText style={styles.label}>Market</ThemedText>
            <TouchableOpacity
              style={[styles.pickerTrigger, { borderColor: colors.border }]}
              onPress={() => setShowMarketPicker(true)}
            >
              <ThemedText style={marketName ? {} : { color: colors.placeholder }}>
                {marketName || "Select a market..."}
              </ThemedText>
              {marketId && (
                <TouchableOpacity
                  onPress={() => {
                    setMarketId(null);
                    setMarketName(null);
                  }}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Market Picker Modal */}
      {showMarketPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <MarketPicker
              onPick={async (selectedMarketId) => {
                setMarketId(selectedMarketId);
                // Fetch market name for display
                const { data: market } = await supabase
                  .schema("lists")
                  .from("markets")
                  .select("name")
                  .eq("id", selectedMarketId)
                  .single();
                setMarketName(market?.name || null);
                setShowMarketPicker(false);
              }}
              onCancel={() => setShowMarketPicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  content: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
  label: {
    fontSize: 14,
    opacity: 0.7,
    fontWeight: "500",
  },
  input: {
    marginBottom: 0,
  },
  pickerTrigger: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: 8,
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
});
